import { create } from 'zustand';

interface EvaluationTask {
  id: string;
  userId: string;
  instanceId: string;
  status: 'pending' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface SongPair {
  id: string;
  track_prev: string;
  track_next: string;
  genre: string;
  versions: Array<{
    id: string;
    name: string;
    audio_path: string;
    audioUrl?: string;
  }>;
  trackPrevInfo?: any;
  trackNextInfo?: any;
  isFamiliar?: boolean;
}

interface EvaluationState {
  currentTask: EvaluationTask | null;
  songPairs: SongPair[];
  currentSongPairIndex: number;
  familiaritySubmitted: Map<string, boolean>;
  ratingsSubmitted: Map<string, boolean>;
  
  setCurrentTask: (task: EvaluationTask) => void;
  setSongPairs: (pairs: SongPair[]) => void;
  setCurrentSongPairIndex: (index: number) => void;
  markFamiliaritySubmitted: (songPairId: string) => void;
  markRatingsSubmitted: (songPairId: string) => void;
  getCurrentSongPair: () => SongPair | null;
  getProgress: () => { current: number; total: number; percentage: number };
  resetEvaluation: () => void;
}

export const useEvaluationStore = create<EvaluationState>((set, get) => ({
  currentTask: null,
  songPairs: [],
  currentSongPairIndex: 0,
  familiaritySubmitted: new Map(),
  ratingsSubmitted: new Map(),
  
  setCurrentTask: (task) => set({ currentTask: task }),
  
  setSongPairs: (pairs) => set({ songPairs: pairs }),
  
  setCurrentSongPairIndex: (index) => set({ currentSongPairIndex: index }),
  
  markFamiliaritySubmitted: (songPairId) => {
    const map = new Map(get().familiaritySubmitted);
    map.set(songPairId, true);
    set({ familiaritySubmitted: map });
  },
  
  markRatingsSubmitted: (songPairId) => {
    const map = new Map(get().ratingsSubmitted);
    map.set(songPairId, true);
    set({ ratingsSubmitted: map });
  },
  
  getCurrentSongPair: () => {
    const { songPairs, currentSongPairIndex } = get();
    return songPairs[currentSongPairIndex] || null;
  },
  
  getProgress: () => {
    const { songPairs, currentSongPairIndex, ratingsSubmitted } = get();
    const completed = Array.from(ratingsSubmitted.values()).filter(Boolean).length;
    return {
      current: completed,
      total: songPairs.length,
      percentage: songPairs.length > 0 ? (completed / songPairs.length) * 100 : 0,
    };
  },
  
  resetEvaluation: () => {
    set({
      currentTask: null,
      songPairs: [],
      currentSongPairIndex: 0,
      familiaritySubmitted: new Map(),
      ratingsSubmitted: new Map(),
    });
  },
}));

